import { useEffect, useState } from "react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { Common } from "@/components";
import { useSearchProducts } from "../services/exclusiveLists";

interface Props {
  productIds: string[];
  lineCount: number;
  onSelect: (product: any) => void;
}

export const SearchProductsSelectbox = ({
  productIds,
  lineCount,
  onSelect,
}: Props) => {
  const [searchKeywords, setSearchKeywords] = useState("");
  const [controller, setController] = useState(() => new AbortController());

  const searchMuta = useSearchProducts({ controller, setController });
  const { data, isLoading } = searchMuta;

  const delaySearch = debounce((kw: string) => {
    if (kw.length > 1) {
      searchMuta.mutate(kw);
    }
  }, 700);

  useEffect(() => {
    if (!searchKeywords && data) searchMuta.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywords]);

  useEffect(() => {
    delaySearch(searchKeywords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywords]);

  const handleSelect = (selected: any) => {
    onSelect(selected);
    setSearchKeywords("");
    searchMuta.reset();
  };

  const Item = ({ item }: any) => (
    <div
      className={clsx(
        "w-full flex flex-row items-center cursor-pointer px-3 py-4 select-none shadow-overline",
        "hover:bg-neutral-5",
        productIds.includes(item.id) ? "bg-blue-10" : ""
      )}
    >
      <div className="basis-1/4">{item.sku}</div>
      <div className="basis-1/2">
        <div className="flex items-center">
          <img
            alt=""
            loading="lazy"
            className="mr-2 w-8 h-8 object-cover d-inline-block"
            src={item.productImageUrl}
          />
          <p>{item.name}</p>
        </div>
      </div>
      <div className="basis-1/4">
        {item.isPriceTBC ? "TBC" : `$${item.cost}/${item.unit}`}
      </div>
    </div>
  );

  const header = () => (
    <div className="w-full flex flex-row px-3 py-2 select-none shadow-overline">
      <div className="basis-1/4 font-bold">SKU</div>
      <div className="basis-1/2 font-bold">PRODUCT NAME</div>
      <div className="basis-1/4 font-bold">UNIT PRICE</div>
    </div>
  );

  return (
    <Common.CustomCombobox
      data={data as any}
      isLoading={isLoading}
      value={searchKeywords}
      optionsHeader={header}
      renderItem={Item}
      placeholder="To add a product, search for SKU or product name..."
      keySearch="name"
      onSelect={handleSelect}
      onChangeInput={(input: string | undefined) =>
        setSearchKeywords(input ? input.trim() : "")
      }
      optionsClassName={clsx(
        lineCount > 4 ? "-top-2 transform -translate-y-full" : "",
        "min-w-[600px]"
      )}
      useSection
      sectionProp="productCategory"
      sectionClassName="px-3 py-2 shadow-overline bg-neutral-10"
      noDataDesc={
        searchKeywords.length > 1
          ? ""
          : "Type 2 more characters for searching products"
      }
    />
  );
};
